import { motion } from 'framer-motion'

import { styled } from 'styled-system/jsx'

const TriggerButton = styled('button', {
  base: {
    fontFamily: 'body',
    fontSize: '13px',
    fontWeight: '700',
    color: 'quickpass.primary',
    backgroundColor: 'transparent',
    border: '1.5px solid',
    borderColor: 'quickpass.primary',
    borderRadius: 'pill',
    paddingX: '14px',
    paddingY: '8px',
    cursor: 'pointer',
    transition:
      'background-color {durations.fast} {easings.appleStandard}, color {durations.fast} {easings.appleStandard}',
    _hover: {
      backgroundColor: 'quickpass.primary',
      color: '#FFFFFF',
    },
  },
})

const Backdrop = styled(motion.div, {
  base: {
    position: 'fixed',
    inset: '0',
    backgroundColor: 'rgba(26, 19, 48, 0.45)',
    zIndex: '105',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px',
  },
})

const Card = styled(motion.div, {
  base: {
    width: '100%',
    maxWidth: '640px',
    maxHeight: '88vh',
    backgroundColor: '#FFFFFF',
    borderRadius: 'lg',
    boxShadow: '0 28px 80px -20px rgba(26, 19, 48, 0.32)',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
  },
})

const Header = styled('header', {
  base: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingX: '22px',
    paddingY: '16px',
    borderBottom: '1px solid',
    borderColor: '#F0EDFA',
  },
})

const Title = styled('h2', {
  base: {
    fontFamily: 'display',
    fontSize: '18px',
    fontWeight: '700',
    color: 'quickpass.ink',
    margin: '0',
    letterSpacing: '-0.3px',
  },
})

const CloseButton = styled('button', {
  base: {
    width: '36px',
    height: '36px',
    borderRadius: 'pill',
    border: 'none',
    backgroundColor: 'quickpass.surfaceCool',
    color: 'quickpass.inkSoft',
    fontSize: '18px',
    lineHeight: '1',
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition:
      'background-color {durations.fast} {easings.appleStandard}, color {durations.fast} {easings.appleStandard}',
    _hover: { backgroundColor: 'quickpass.warmth', color: '#FFFFFF' },
  },
})

const Body = styled('div', {
  base: {
    overflowY: 'auto',
    padding: '20px 22px',
    display: 'flex',
    flexDirection: 'column',
    gap: '14px',
  },
})

const Field = styled('div', {
  base: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  },
})

const FieldLabel = styled('span', {
  base: {
    fontFamily: 'body',
    fontSize: '11px',
    fontWeight: '700',
    letterSpacing: '0.5px',
    textTransform: 'uppercase',
    color: 'quickpass.inkSoft',
  },
})

const SubjectInput = styled('input', {
  base: {
    fontFamily: 'body',
    fontSize: '14px',
    fontWeight: '600',
    color: 'quickpass.ink',
    backgroundColor: 'quickpass.surfaceCool',
    border: '1.5px solid',
    borderColor: '#E5E0F5',
    borderRadius: 'md',
    paddingX: '14px',
    paddingY: '10px',
    transition:
      'border-color {durations.fast} {easings.appleStandard}, background-color {durations.fast} {easings.appleStandard}',
    _focus: {
      outline: 'none',
      borderColor: 'quickpass.primary',
      backgroundColor: '#FFFFFF',
    },
  },
})

const BodyTextarea = styled('textarea', {
  base: {
    fontFamily: 'body',
    fontSize: '14px',
    color: 'quickpass.ink',
    backgroundColor: 'quickpass.surfaceCool',
    border: '1.5px solid',
    borderColor: '#E5E0F5',
    borderRadius: 'md',
    paddingX: '14px',
    paddingY: '12px',
    minHeight: '240px',
    lineHeight: '1.55',
    resize: 'vertical',
    transition:
      'border-color {durations.fast} {easings.appleStandard}, background-color {durations.fast} {easings.appleStandard}',
    _focus: {
      outline: 'none',
      borderColor: 'quickpass.primary',
      backgroundColor: '#FFFFFF',
    },
  },
})

const Footer = styled('footer', {
  base: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingX: '22px',
    paddingY: '14px',
    borderTop: '1px solid',
    borderColor: '#F0EDFA',
    backgroundColor: 'quickpass.surfaceCool',
  },
})

const Hint = styled('span', {
  base: {
    fontFamily: 'body',
    fontSize: '11px',
    color: 'quickpass.inkSoft',
    fontStyle: 'italic',
  },
})

const FooterActions = styled('div', {
  base: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
})

const SecondaryButton = styled('button', {
  base: {
    fontFamily: 'body',
    fontSize: '13px',
    fontWeight: '600',
    color: 'quickpass.inkSoft',
    backgroundColor: 'transparent',
    border: '1.5px solid',
    borderColor: '#E5E0F5',
    borderRadius: 'pill',
    paddingX: '14px',
    paddingY: '8px',
    cursor: 'pointer',
    transition:
      'border-color {durations.fast} {easings.appleStandard}, color {durations.fast} {easings.appleStandard}',
    _hover: { borderColor: 'quickpass.primary', color: 'quickpass.primary' },
  },
})

const PrimaryButton = styled('button', {
  base: {
    fontFamily: 'body',
    fontSize: '13px',
    fontWeight: '700',
    color: '#FFFFFF',
    backgroundColor: 'quickpass.primary',
    border: 'none',
    borderRadius: 'pill',
    paddingX: '16px',
    paddingY: '9px',
    cursor: 'pointer',
    transition:
      'transform {durations.fast} {easings.appleStandard}, box-shadow {durations.fast} {easings.appleStandard}',
    _hover: {
      transform: 'translateY(-1px)',
      boxShadow: '0 12px 28px -10px rgba(128, 84, 255, 0.5)',
    },
    _disabled: {
      opacity: '0.5',
      cursor: 'not-allowed',
      _hover: { transform: 'none', boxShadow: 'none' },
    },
  },
})

const StatusText = styled('div', {
  base: {
    fontFamily: 'body',
    fontSize: '13px',
    color: 'quickpass.inkSoft',
    fontStyle: 'italic',
    textAlign: 'center',
    paddingY: '40px',
  },
})

const ErrorBanner = styled('div', {
  base: {
    fontFamily: 'body',
    fontSize: '13px',
    color: 'quickpass.warmth',
    fontWeight: '600',
  },
})

const CopiedFlash = styled('span', {
  base: {
    fontFamily: 'body',
    fontSize: '12px',
    fontWeight: '700',
    color: 'quickpass.success',
  },
})

export {
  TriggerButton,
  Backdrop,
  Card,
  Header,
  Title,
  CloseButton,
  Body,
  Field,
  FieldLabel,
  SubjectInput,
  BodyTextarea,
  Footer,
  Hint,
  FooterActions,
  SecondaryButton,
  PrimaryButton,
  StatusText,
  ErrorBanner,
  CopiedFlash,
}
